// Base Charts — read-only on-chain charting for the Base L2 ecosystem.
// Port of coinbaseterm/public/assets/app.js into the tab contract; talks to
// the worker's public /api/base/* (KV-cached GeckoTerminal, DEX Screener
// search fallback). No wallet, no signing — execution is a separate project
// by design (split-on-signing, 2026-06-11).
// Chart rendering: lightweight-charts standalone, loaded as a global from
// jsDelivr in index.html (NOT cdnjs — it isn't hosted there).

import type { Tab } from './index';
import { workerFetch } from '../worker';
import { esc } from '../utils';

declare const LightweightCharts: any;

interface BasePool {
  pool_address: string;
  name: string;
  dex: string | null;
  price_usd: string | null;
  reserve_usd: number;
  vol24h_usd: number | null;
  change_h24_pct: string | number | null;
  source?: string;
}
interface PairsResp { pools: BasePool[]; count: number; min_liquidity_usd: number; stale?: boolean; error?: string; }
type Candle = [number, number, number, number, number, number];
interface OhlcvResp { candles: Candle[]; count: number; stale?: boolean; error?: string; }

const fmtUsd   = (x: number | null) => x == null ? '—' : '$' + Number(x).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtPrice = (x: string | number | null) => x == null ? '—' : '$' + Number(x).toLocaleString(undefined, { maximumSignificantDigits: 6 });

export class BaseChartsTab implements Tab {
  readonly id = 'base';
  private chart: any = null;
  private candleSeries: any = null;
  private volumeSeries: any = null;
  private pools: BasePool[] = [];
  private current: BasePool | null = null;
  private tf = { tf: 'hour', agg: '1' };
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private loaded = false;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    this.el('base-search').addEventListener('input', e => {
      if (this.debounce) clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.search((e.target as HTMLInputElement).value.trim()), 400);
    });
    this.el('base-tfbtns').addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-tf]');
      if (!btn) return;
      this.el('base-tfbtns').querySelectorAll('button').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      this.tf = { tf: btn.dataset.tf!, agg: btn.dataset.agg! };
      void this.loadChart();
    });
    this.el('base-pools').addEventListener('click', e => {
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-pool-idx]');
      if (!row) return;
      this.el('base-pools').querySelectorAll('.base-pool').forEach(d => d.classList.remove('active'));
      row.classList.add('active');
      void this.select(this.pools[Number(row.dataset.poolIdx)]);
    });
  }

  render(): void {
    // Chart must be created while the section is visible and sized.
    if (!this.chart && typeof LightweightCharts !== 'undefined') {
      const host = this.el('base-chart');
      this.chart = LightweightCharts.createChart(host, {
        layout: { background: { color: 'transparent' }, textColor: '#6b7385' },
        grid: { vertLines: { color: 'rgba(255,255,255,0.04)' }, horzLines: { color: 'rgba(255,255,255,0.04)' } },
        timeScale: { timeVisible: true, secondsVisible: false, borderColor: 'rgba(255,255,255,0.08)' },
        rightPriceScale: { borderColor: 'rgba(255,255,255,0.08)' },
        height: 420,
      });
      this.candleSeries = this.chart.addCandlestickSeries({
        upColor: '#26a69a', downColor: '#ef5350',
        wickUpColor: '#26a69a', wickDownColor: '#ef5350', borderVisible: false,
      });
      this.volumeSeries = this.chart.addHistogramSeries({ priceScaleId: '', priceFormat: { type: 'volume' } });
      this.volumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
      new ResizeObserver(() => this.chart.applyOptions({ width: host.clientWidth })).observe(host);
    }
    if (!this.loaded) { this.loaded = true; void this.search(''); }
  }

  private async fetchPairs(query: string): Promise<PairsResp> {
    const qs = new URLSearchParams();
    if (query) qs.set('query', query);
    return workerFetch<PairsResp>(`/api/base/pairs?${qs}`);
  }

  private async search(query: string): Promise<void> {
    this.status(query ? `searching "${query}"…` : 'loading trending…');
    try {
      const r = await this.fetchPairs(query);
      if (r.error) throw new Error(r.error);
      this.pools = r.pools;
      this.renderPools(r);
      this.status('');
    } catch (e) {
      this.el('base-pools').innerHTML = `<div style="padding:12px;color:var(--red);font-size:12px;">${esc((e as Error).message)}</div>`;
      this.status('');
    }
  }

  private renderPools(r: PairsResp): void {
    if (!r.pools.length) {
      this.el('base-pools').innerHTML = '<div style="padding:12px;color:var(--text3);font-size:12px;">No pools above the liquidity floor.</div>';
      return;
    }
    this.el('base-pools').innerHTML = r.pools.map((p, i) => `
      <div class="base-pool" data-pool-idx="${i}" style="padding:8px 12px;border-bottom:1px solid var(--border);cursor:pointer;">
        <div style="color:var(--text);font-size:12px;">${esc(p.name)} <span style="color:var(--text3);font-size:11px;">${esc(p.dex ?? '')}</span></div>
        <div style="display:flex;justify-content:space-between;font-size:11px;color:var(--text3);margin-top:2px;">
          <span>liq ${fmtUsd(p.reserve_usd)} · vol24h ${fmtUsd(p.vol24h_usd)}</span>
          <span style="color:${Number(p.change_h24_pct) >= 0 ? 'var(--green)' : 'var(--red)'};">${p.change_h24_pct == null ? '—' : Number(p.change_h24_pct).toFixed(2) + '%'}</span>
        </div>
      </div>`).join('')
      + (r.stale ? '<div style="padding:8px 12px;color:var(--yellow,#e0b040);font-size:11px;">⚠ stale data — upstream throttled, list is up to 24h old</div>' : '');
  }

  private async select(pool: BasePool): Promise<void> {
    this.current = pool;
    this.el('base-pairname').textContent = pool.name;
    this.el('base-pairstats').textContent =
      `price ${fmtPrice(pool.price_usd)} · liquidity ${fmtUsd(pool.reserve_usd)} · vol24h ${fmtUsd(pool.vol24h_usd)} · ${pool.pool_address.slice(0, 10)}…`;
    await this.loadChart();
  }

  private async loadChart(retried = false): Promise<void> {
    if (!this.current || !this.chart) return;
    this.status('loading candles…');
    try {
      const qs = new URLSearchParams({ pool_address: this.current.pool_address, timeframe: this.tf.tf, aggregate: this.tf.agg, limit: '200' });
      const r = await workerFetch<OhlcvResp>(`/api/base/ohlcv?${qs}`);
      if (r.error) throw new Error(r.error);
      this.candleSeries.setData(r.candles.map(c => ({ time: c[0], open: c[1], high: c[2], low: c[3], close: c[4] })));
      this.volumeSeries.setData(r.candles.map(c => ({ time: c[0], value: c[5], color: c[4] >= c[1] ? '#26a69a55' : '#ef535055' })));
      this.chart.timeScale().fitContent();
      this.status(`${r.candles.length} candles${r.stale ? ' · ⚠ stale (upstream throttled)' : ''} · pool price, not asset price — deepest pool listed first`);
    } catch (e) {
      if (!retried) {       // upstream 429s on cold pools are transient — one quiet retry
        await new Promise(res => setTimeout(res, 1500));
        return this.loadChart(true);
      }
      this.status('candles failed: ' + (e as Error).message, true);
    }
  }

  private status(msg: string, isErr = false): void {
    const el = this.el('base-status');
    el.textContent = msg;
    el.style.color = isErr ? 'var(--red)' : 'var(--text3)';
  }
}
